import {
  parseUnits,
  formatUnits,
  BrowserProvider,
  Contract,
  isAddress,
  MaxInt256,
} from "ethers";
import { CurrencyAmount, Percent } from "@uniswap/sdk-core";
import { TickMath, TICK_SPACINGS } from "@uniswap/v3-sdk";
import Quoter from "@uniswap/v3-periphery/artifacts/contracts/lens/Quoter.sol/Quoter.json";
import {
  QUOTER_CONTRACT_ADDRESS,
  USDT_TOKEN,
  XBR_TOKEN,
  TBA_TOKEN,
} from "../web3Utils";
import {
  nearestUsableTick,
  NonfungiblePositionManager,
  Pool,
  Position,
} from "@uniswap/v3-sdk";
import { getLiquidity } from "./liquidity";
import { CurrentConfig } from "./config";
import {
  ERC20_ABI,
  MAX_FEE_PER_GAS,
  MAX_PRIORITY_FEE_PER_GAS,
  NONFUNGIBLE_POSITION_MANAGER_ABI,
  NONFUNGIBLE_POSITION_MANAGER_CONTRACT_ADDRESS,
} from "../web3Utils";
import { fromReadableAmount, fromReadableAmount1 } from "./conversion";
import { findPoolAddress, getPoolInfo } from "./pool";
import { fetchTokenObject } from "./liquidity";
import { connectWallet, getProviders, subscribeToProviders } from "./farm";
import { JsonRpcProvider } from 'ethers';
import { getEthersSigner } from '../utils/wagmi.utils';
const positionManagerAddress = "0xC36442b4a4522E871399CD717aBDD847Ab11FE88";

// NonfungiblePositionManager ABI
const positionManagerAbi = [
  "function positions(uint256 tokenId) view returns (" +
    "uint96 nonce, address operator, address token0, address token1, uint24 fee, " +
    "int24 tickLower, int24 tickUpper, uint128 liquidity, " +
    "uint256 feeGrowthInside0LastX128, uint256 feeGrowthInside1LastX128, " +
    "uint128 tokensOwed0, uint128 tokensOwed1)",
  "function collect(tuple(uint256 tokenId, address recipient, uint128 amount0Max, uint128 amount1Max) params) " +
    "payable returns (uint256 amount0, uint256 amount1)",
];

export async function getTokenTransferApproval(token, value,
	signer,
	) {
  try {
    const tokenContract = new Contract(token.address, ERC20_ABI, signer);

    const approveTx = await tokenContract.approve(
      NONFUNGIBLE_POSITION_MANAGER_CONTRACT_ADDRESS,
      value
    );
    return approveTx.hash;
  } catch (error) {
    console.error(error);
    return "Failed";
  }
}

export async function constructPosition(
  token0Amount,
  token1Amount,
  poolAddress,
  walletProvider
) {
  try {
    // get pool info
    const poolInfo = await getPoolInfo(poolAddress, walletProvider);

    // construct pool instance
    const configuredPool = new Pool(
      token0Amount.currency,
      token1Amount.currency,
      poolInfo.fee,
      poolInfo.sqrtPriceX96.toString(),
      poolInfo.liquidity.toString(),
      poolInfo.tick
    );

    // create position using the maximum liquidity from input amounts
    return Position.fromAmounts({
      pool: configuredPool,
      tickLower: nearestUsableTick(
        TickMath.MIN_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      tickUpper: nearestUsableTick(
        TickMath.MAX_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      amount0: token0Amount.quotient,
      amount1: token1Amount.quotient,
      useFullPrecision: true,
    });
  } catch (error) {
    console.error("Error at constructPosition", error);
    return null;
  }
}

export async function constructPosition1(
  positionId,
  token0Amount,
  token1Amount,
  poolAddress,
  jsonRpcProvider
) {
  try {
    const poolInfo = await getPoolInfo(poolAddress, jsonRpcProvider);

    const pool = new Pool(
      token0Amount.currency,
      token1Amount.currency,
      poolInfo.fee,
      poolInfo.sqrtPriceX96.toString(),
      poolInfo.liquidity.toString(),
      poolInfo.tick
    );

    const configuredPool = await getLiquidity(positionId, jsonRpcProvider);
    return Position.fromAmounts({
      pool: pool,
      tickLower: Number(configuredPool.tickLower),
      tickUpper: Number(configuredPool.tickUpper),
      amount0: token0Amount.quotient,
      amount1: token1Amount.quotient,
      useFullPrecision: true,
    });
  } catch (error) {
    console.error("Error at constructPosition", error);
    return null;
  }
}

export async function modifyPositionnew(
  positionId,
  token0name,
  token1name,
  token0Amount,
  token1Amount,
  jsonRpcProvider,
	signer,
	walletAddress
) {
  try {
    let token0, token1;
    if (
      (token0name === "USDT" && token1name === "XBR") ||
      (token0name === "XBR" && token1name === "USDT")
    ) {
      token0 = USDT_TOKEN;
      token1 = XBR_TOKEN;
    } else if (
      (token0name === "USDT" && token1name === "TBA") ||
      (token0name === "TBA" && token1name === "USDT")
    ) {
      token0 = TBA_TOKEN;
      token1 = USDT_TOKEN;
    } else {
      token0 = TBA_TOKEN;
      token1 = XBR_TOKEN;
    }


    const { poolAddress } = await findPoolAddress(
      token0.address,
      token1.address,
      jsonRpcProvider
    );

    const positionToIncreaseBy = await constructPosition1(
      positionId,
      CurrencyAmount.fromRawAmount(
        token0,
        fromReadableAmount1(Number(token0Amount), token0.decimals)
      ),
      CurrencyAmount.fromRawAmount(
        token1,
        fromReadableAmount1(Number(token1Amount), token1.decimals)
      ),
      poolAddress,
      jsonRpcProvider
    );

    const addLiquidityOptions = {
      deadline: Math.floor(Date.now() / 1000) + 60 * 20,
      slippageTolerance: new Percent(50, 10000),
      tokenId: positionId,
    };

    const { calldata, value } = NonfungiblePositionManager.addCallParameters(
      positionToIncreaseBy,
      addLiquidityOptions
    );

    const fees = await jsonRpcProvider.getFeeData();
    const maxFeePerGas = fees.maxFeePerGas || parseUnits("100", "gwei");
    const maxPriorityFeePerGas =
      fees.maxPriorityFeePerGas || parseUnits("1", "gwei");


    // build transaction
    const transaction = {
      data: calldata,
      to: NONFUNGIBLE_POSITION_MANAGER_CONTRACT_ADDRESS,
      value: value,
      from: walletAddress,
      maxFeePerGas: maxFeePerGas,
      maxPriorityFeePerGas: maxPriorityFeePerGas,
    };
    console.log("transaction", transaction);

    const tx = await signer?.sendTransaction(transaction);

    const receipt = await tx.wait();
    return receipt.status !== null ? tx.hash : null;
  } catch (error) {
    console.error("Error at minting position:", error);
  }
}
export async function getPositionIds(walletAddress, jsonRpcProvider) {
  try {
    const positionContract = new Contract(
      NONFUNGIBLE_POSITION_MANAGER_CONTRACT_ADDRESS,
      NONFUNGIBLE_POSITION_MANAGER_ABI,
      jsonRpcProvider
    );

    // Fetch the position data
    const positionManagerContract = new Contract(
      positionManagerAddress,
      positionManagerAbi,
      jsonRpcProvider
    );

    // Get number of positions
    const balance = await positionContract.balanceOf(walletAddress);

    // Get all positions
    const tokenIds = [];
    for (let i = 0; i < balance; i++) {
      const tokenOfOwnerByIndex = await positionContract.tokenOfOwnerByIndex(
        walletAddress,
        i
      );

     

      // Fetch the position
      const position = await positionManagerContract.positions(
        tokenOfOwnerByIndex
      );
      const { token0, token1 } = position;

      const { poolAddress } = await findPoolAddress(token0, token1,jsonRpcProvider);
      if (
        poolAddress === "0x1eb8DFe76eE03Fd68Aa58f849CAD5590B4D12617" ||
        poolAddress === "0x58f4112B2f44185D2613313bF2F0C6736A2717A6" ||
        poolAddress === "0xd6A31b81199F0281bE70Caf02Af7a9A04522B48F"
      )
        tokenIds.push(tokenOfOwnerByIndex.toString());
    }

    return tokenIds;
  } catch (error) {
    console.error("Error at getPositionIds:", error);
  }
}

export async function getFarmPositionIds(walletAddress,jsonRpcProvider) {
  try {
    const positionContract = new Contract(
      NONFUNGIBLE_POSITION_MANAGER_CONTRACT_ADDRESS,
      NONFUNGIBLE_POSITION_MANAGER_ABI,
      jsonRpcProvider
    );

    // Fetch the position data
    const positionManagerContract = new Contract(
      positionManagerAddress,
      positionManagerAbi,
      jsonRpcProvider
    );

    // Get number of positions
    const balance = await positionContract.balanceOf(walletAddress);

    // Get all positions
    const tokenIds = [];
    for (let i = 0; i < balance; i++) {
      const tokenOfOwnerByIndex = await positionContract.tokenOfOwnerByIndex(
        walletAddress,
        i
      );

      // Fetch the position
      const position = await positionManagerContract.positions(
        tokenOfOwnerByIndex
      );
      const { token0, token1 } = position;

      const { poolAddress } = await findPoolAddress(token0, token1);
      if (poolAddress === "0x1eb8DFe76eE03Fd68Aa58f849CAD5590B4D12617")
        tokenIds.push(tokenOfOwnerByIndex.toString());
    }

    return tokenIds;
  } catch (error) {
    console.error("Error at getPositionIds:", error);
  }
}

export async function quoteIn(
  poolAddress,
  amountIn,
  inDecimals,
  outDecimals,
  jsonRpcProvider
) {
  try {
    const quoterContract = new Contract(
      QUOTER_CONTRACT_ADDRESS,
      Quoter.abi,
      jsonRpcProvider
    );
    const poolConstants = await getPoolInfo(poolAddress, jsonRpcProvider);

    const quotedAmount = await quoterContract.quoteExactInputSingle.staticCall(
      poolConstants.token0,
      poolConstants.token1,
      poolConstants.fee,
      parseUnits(
        parseFloat(amountIn).toFixed(inDecimals),
        inDecimals === 18 ? 18 : 6
      ).toString(),
      0
    );

    const outAmount = formatUnits(quotedAmount, outDecimals);
    return outAmount;
  } catch (error) {
    console.error("Error at quote:", error);
  }
}

export async function quoteOut(
  poolAddress,
  amountIn,
  inDecimals,
  outDecimals,
  jsonRpcProvider
) {
  try {
    const quoterContract = new Contract(
      QUOTER_CONTRACT_ADDRESS,
      Quoter.abi,
      jsonRpcProvider
    );
    const poolConstants = await getPoolInfo(poolAddress, jsonRpcProvider);

    const quotedAmount = await quoterContract.quoteExactInputSingle.staticCall(
      poolConstants.token1,
      poolConstants.token0,
      poolConstants.fee,
      parseUnits(amountIn.toString(), inDecimals === 18 ? 18 : 6).toString(), // Updated for ethers v6
      0
    );

    return formatUnits(quotedAmount, outDecimals);
  } catch (error) {
    console.error("Error at quote:", error);
  }
}


export async function mintPositionNew(
  token0Amount,
  token1Amount,
  token0name,
  token1name,
  jsonRpcProvider,
	walletAddress,
	signer
) {
  try {
    let token0, token1;
    if (
      (token0name === "USDT" && token1name === "XBR") ||
      (token0name === "XBR" && token1name === "USDT")
    ) {
      token0 = USDT_TOKEN;
      token1 = XBR_TOKEN;
    } else if (
      (token0name === "USDT" && token1name === "TBA") ||
      (token0name === "TBA" && token1name === "USDT")
    ) {
      token0 = TBA_TOKEN;
      token1 = USDT_TOKEN;
    } else {
      token0 = TBA_TOKEN;
      token1 = XBR_TOKEN;
    }

    const { poolAddress } = await findPoolAddress(
      token0.address,
      token1.address,
      jsonRpcProvider
    );

    const positionToMint = await constructPosition(
      CurrencyAmount.fromRawAmount(
        token0,
        fromReadableAmount1(Number(token0Amount), token0.decimals)
      ),
      CurrencyAmount.fromRawAmount(
        token1,
        fromReadableAmount1(Number(token1Amount), token1.decimals)
      ),
      poolAddress,
      jsonRpcProvider
    );


    const mintOptions = {
      recipient: walletAddress,
      deadline: Math.floor(Date.now() / 1000) + 60 * 20,
      slippageTolerance: new Percent(50, 10_000),
    };

    const { calldata, value } = NonfungiblePositionManager.addCallParameters(
      positionToMint,
      mintOptions
    );

    const fees = await jsonRpcProvider.getFeeData();
    const maxFeePerGas = fees.maxFeePerGas || parseUnits("100", "gwei");
    const maxPriorityFeePerGas =
      fees.maxPriorityFeePerGas || parseUnits("1", "gwei");

    const transaction = {
      data: calldata,
      to: NONFUNGIBLE_POSITION_MANAGER_CONTRACT_ADDRESS,
      value: value || 0,
      from: walletAddress,
      maxFeePerGas: maxFeePerGas,
      maxPriorityFeePerGas: maxPriorityFeePerGas,
    };

    let gasLimit = await jsonRpcProvider.estimateGas(transaction);
    transaction.gasLimit = gasLimit;

    const tx = await signer.sendTransaction(transaction);
    const receipt = await tx.wait();

    if (receipt.status === 1) {
      console.log("Transaction was successful!");
    } else {
      console.log("Transaction failed.");
    }
    console.log("receipt", receipt);
    return tx.hash;
  } catch (error) {
    console.error("Error at minting position:", error);
    return "Failed";
  }
}

export async function removeLiquiditynew(
  token0Amount,
  token1Amount,
  token0name,
  token1name,
  positionId,
  jsonRpcProvider,
  setIsConfirmModalOpen,
  setIsRemoveConfirmed,
  setIsTransactionFailed,
  setError,
  setHashdata,
  setIsLiquidityChanged,
  setRemoveLiquidity,
	walletAddress
) {
  if (positionId.length === 0) {
    alert("No Positions Selected!");
    return;
  }
  setIsConfirmModalOpen(true);
	const signer = await getEthersSigner()

  try {
    let token0, token1;
    if (
      (token0name === "USDT" && token1name === "XBR") ||
      (token0name === "XBR" && token1name === "USDT")
    ) {
      token0 = USDT_TOKEN;
      token1 = XBR_TOKEN;
    } else if (
      (token0name === "USDT" && token1name === "TBA") ||
      (token0name === "TBA" && token1name === "USDT")
    ) {
      token0 = TBA_TOKEN;
      token1 = USDT_TOKEN;
    } else {
      token0 = XBR_TOKEN;
      token1 = TBA_TOKEN;
    }

    const { poolAddress, fee } = await findPoolAddress(
      token0.address,
      token1.address,
      jsonRpcProvider
    );

    const currentPosition = await constructPosition1(
      positionId,
      CurrencyAmount.fromRawAmount(
        token0,
        fromReadableAmount1(Number(token0Amount), token0.decimals)
      ),
      CurrencyAmount.fromRawAmount(
        token1,
        fromReadableAmount1(Number(token1Amount), token1.decimals)
      ),
      poolAddress,
      jsonRpcProvider
    );

    const collectOptions = {
      expectedCurrencyOwed0: CurrencyAmount.fromRawAmount(token0, 0),
      expectedCurrencyOwed1: CurrencyAmount.fromRawAmount(token1, 0),
      recipient: walletAddress,
    };

    const removeLiquidityOptions = {
      deadline: Math.floor(Date.now() / 1000) + 60 * 20,
      slippageTolerance: new Percent(5, 100),
      tokenId: positionId,
      liquidityPercentage: new Percent(1),
      collectOptions,
    };
    // get calldata for minting a position
    const { calldata, value } = NonfungiblePositionManager.removeCallParameters(
      currentPosition,
      removeLiquidityOptions
    );

    const fees = await jsonRpcProvider.getFeeData();
    const maxFeePerGas = fees.maxFeePerGas || parseUnits("100", "gwei");
    const maxPriorityFeePerGas =
      fees.maxPriorityFeePerGas || parseUnits("1", "gwei");

    // build transaction
    const transaction = {
      data: calldata,
      to: NONFUNGIBLE_POSITION_MANAGER_CONTRACT_ADDRESS,
      value: value,
      from: walletAddress,
      maxFeePerGas: maxFeePerGas, // formatUnits(fees.maxFeePerGas, "wei"),
      maxPriorityFeePerGas: maxPriorityFeePerGas, // formatUnits(fees.maxPriorityFeePerGas, "wei"),
    };
    let gasLimit = await signer.estimateGas(transaction);
    transaction.gasLimit = gasLimit;

    const tx = await signer?.sendTransaction(transaction);
    const receipt = await tx.wait();
    // Check transaction status
    if (receipt.status === 1) {
      setHashdata(tx.hash);
      setIsConfirmModalOpen(false);
      setIsRemoveConfirmed(true);
      setIsLiquidityChanged(true);
      setTimeout(() => setRemoveLiquidity(false), 5000);
    } else {
      console.log("Transaction failed.");
    }
    return tx.hash;
  } catch (error) {
    setIsConfirmModalOpen(false);
    setIsTransactionFailed(true);
    setError(error.message);
    console.error("Error at removing liquidity:", error);
  }
}

export async function getEstimatedValueForLiquidity(
  token,
  amount,
  walletProvider
) {
  const { poolAddress } = await findPoolAddress(
    USDT_TOKEN.address,
    XBR_TOKEN.address,
    walletProvider
  );

  const poolInfo = await getPoolInfo(poolAddress, walletProvider);

  const tokens = {
    USDT: CurrentConfig.tokens.token0,
    XBR: CurrentConfig.tokens.token1,
  };

  // construct pool instance
  const pool = new Pool(
    token === CurrentConfig.tokens.token0.name ? tokens.USDT : tokens.XBR,
    token === CurrentConfig.tokens.token0.name ? tokens.XBR : tokens.USDT,
    poolInfo.fee,
    poolInfo.sqrtPriceX96.toString(),
    poolInfo.liquidity.toString(),
    poolInfo.tick
  );
  console.log("TOKEN-SELECT", token);
  if (token === USDT_TOKEN.symbol) {
    const position = new Position.fromAmount0({
      pool,
      tickLower: nearestUsableTick(
        TickMath.MIN_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      tickUpper: nearestUsableTick(
        TickMath.MAX_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      amount0: CurrencyAmount.fromRawAmount(
        CurrentConfig.tokens.token0,
        fromReadableAmount(Number(amount), CurrentConfig.tokens.token0.decimals)
      ).quotient,
      useFullPrecision: true, // Recommended
    });
    return position.amount1.toSignificant(6);
  } else {
    const position = new Position.fromAmount1({
      pool,
      tickLower: nearestUsableTick(
        TickMath.MIN_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      tickUpper: nearestUsableTick(
        TickMath.MAX_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      amount1: CurrencyAmount.fromRawAmount(
        CurrentConfig.tokens.token1,
        fromReadableAmount(Number(amount), CurrentConfig.tokens.token1.decimals)
      ).quotient,
      useFullPrecision: true, // Recommended
    });
    return position.amount0.toSignificant(6);
  }
}

export async function getEstimatedValueForLiquidityTBA(
  token,
  amount,
  walletProvider
) {
  const { poolAddress } = await findPoolAddress(
    TBA_TOKEN.address,
    USDT_TOKEN.address,
    walletProvider
  );

  const poolInfo = await getPoolInfo(poolAddress, walletProvider);

  const tokens = {
    USDT: CurrentConfig.tokens.token0,
    TBA: CurrentConfig.tokens.token2, // TBA token assignment
  };

  // Construct pool instance with appropriate tokens
  const pool = new Pool(
    token === TBA_TOKEN.symbol ? tokens.TBA : tokens.USDT,
    token === TBA_TOKEN.symbol ? tokens.USDT : tokens.TBA,
    poolInfo.fee,
    poolInfo.sqrtPriceX96.toString(),
    poolInfo.liquidity.toString(),
    poolInfo.tick
  );
  console.log("TOKEN-SELECT", token);

  // Handle precision for the USDT token
  if (token === USDT_TOKEN.symbol) {
    const position = new Position.fromAmount1({
      pool,
      tickLower: nearestUsableTick(
        TickMath.MIN_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      tickUpper: nearestUsableTick(
        TickMath.MAX_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      amount1: CurrencyAmount.fromRawAmount(
        tokens.USDT, // USDT is the token with amount1
        fromReadableAmount(Number(amount), tokens.USDT.decimals) // Ensuring proper decimal conversion
      ).quotient,
      useFullPrecision: true, // Recommended
    });
    return position.amount0.toSignificant(6); // Return the corresponding TBA value
  } else {
    // Handle precision for the TBA token
    const position = new Position.fromAmount0({
      pool,
      tickLower: nearestUsableTick(
        TickMath.MIN_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      tickUpper: nearestUsableTick(
        TickMath.MAX_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      amount0: CurrencyAmount.fromRawAmount(
        tokens.TBA, // TBA is the token with amount0
        fromReadableAmount(Number(amount), tokens.TBA.decimals) // Ensuring proper decimal conversion
      ).quotient,
      useFullPrecision: true, // Recommended
    });
    return position.amount1.toSignificant(6); // Return the corresponding USDT value
  }
}

export async function getEstimatedValueForLiquiditynew(
  token0name,
  token1name,
  amount,
  jsonRpcProvider
) {
  let token0, token1;
  if (
    (token0name === "USDT" && token1name === "XBR") ||
    (token0name === "XBR" && token1name === "USDT")
  ) {
    token0 = USDT_TOKEN;
    token1 = XBR_TOKEN;
  } else if (
    (token0name === "USDT" && token1name === "TBA") ||
    (token0name === "TBA" && token1name === "USDT")
  ) {
    token0 = TBA_TOKEN;
    token1 = USDT_TOKEN;
  } else {
    token0 = TBA_TOKEN;
    token1 = XBR_TOKEN;
  }

  const { poolAddress } = await findPoolAddress(
    token0.address,
    token1.address,
    jsonRpcProvider
  );

  const poolInfo = await getPoolInfo(poolAddress, jsonRpcProvider);

  // Construct pool instance using dynamic token selection
  const pool = new Pool(
    token0 === token0.symbol ? token0 : token1,
    token0 === token0.symbol ? token1 : token0,
    poolInfo.fee,
    poolInfo.sqrtPriceX96.toString(),
    poolInfo.liquidity.toString(),
    poolInfo.tick
  );

  // Handle precision for token0
  if (token0name === token0.symbol) {
    const position = new Position.fromAmount0({
      pool,
      tickLower: nearestUsableTick(
        TickMath.MIN_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      tickUpper: nearestUsableTick(
        TickMath.MAX_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      amount0: CurrencyAmount.fromRawAmount(
        token0,
        fromReadableAmount(Number(amount), token0.decimals)
      ).quotient,
      useFullPrecision: true, // Recommended
    });
    return position.amount1.toSignificant(6); // Return corresponding token1 value
  } else {
    // Handle precision for token1
    const position = new Position.fromAmount1({
      pool,
      tickLower: nearestUsableTick(
        TickMath.MIN_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      tickUpper: nearestUsableTick(
        TickMath.MAX_TICK,
        TICK_SPACINGS[poolInfo.fee]
      ),
      amount1: CurrencyAmount.fromRawAmount(
        token1,
        fromReadableAmount(Number(amount), token1.decimals)
      ).quotient,
      useFullPrecision: true, // Recommended
    });
    return position.amount0.toSignificant(6); // Return corresponding token0 value
  }
}

export async function collectfees(
  positionId,
  token0name,
  token1name,
  jsonRpcProvider,
	signer,
	walletAddress,
) {
  try {
    const positionManagerContract = new Contract(
      positionManagerAddress,
      positionManagerAbi,
      signer
    );

    let token0, token1;
    if (
      (token0name === "USDT" && token1name === "XBR") ||
      (token0name === "XBR" && token1name === "USDT")
    ) {
      token0 = USDT_TOKEN;
      token1 = XBR_TOKEN;
    } else if (
      (token0name === "USDT" && token1name === "TBA") ||
      (token0name === "TBA" && token1name === "USDT")
    ) {
      token0 = TBA_TOKEN;
      token1 = USDT_TOKEN;
    } else {
      token0 = TBA_TOKEN;
      token1 = XBR_TOKEN;
    }

    const position = await positionManagerContract.positions(positionId);

    const collectOptions = {
      tokenId: positionId,
      expectedCurrencyOwed0: CurrencyAmount.fromRawAmount(
        token0,
        Number(position.tokensOwed0)
      ),
      expectedCurrencyOwed1: CurrencyAmount.fromRawAmount(
        token1,
        Number(position.tokensOwed1)
      ),
      recipient: walletAddress,
    };
    console.log("collect options", collectOptions);
    const { calldata, value } =
      NonfungiblePositionManager.collectCallParameters(collectOptions);

    const fees = await jsonRpcProvider.getFeeData();
    const maxFeePerGas = fees.maxFeePerGas || parseUnits("100", "gwei");
    const maxPriorityFeePerGas =
      fees.maxPriorityFeePerGas || parseUnits("1", "gwei");

    console.log("maxFeePerGas", formatUnits(maxFeePerGas, "gwei"));
    console.log(
      "maxPriorityFeePerGas",
      formatUnits(maxPriorityFeePerGas, "gwei")
    );

    const transaction = {
      data: calldata,
      to: NONFUNGIBLE_POSITION_MANAGER_CONTRACT_ADDRESS,
      value: value,
      from: walletAddress,
      maxFeePerGas: maxFeePerGas,
      maxPriorityFeePerGas: maxPriorityFeePerGas,
    };
    let gasLimit = await jsonRpcProvider.estimateGas(transaction);
    transaction.gasLimit = gasLimit;

    const tx = await signer?.sendTransaction(transaction);
    const receipt = await tx.wait();

    if (receipt.status === 1) {
      console.log("Transaction was successful!");
    } else {
      console.log("Transaction failed.");
    }
    return tx.hash;
  } catch (error) {
    console.error("Error at removing liquidity:", error);
  }
}

export async function getCollectFees(
  tokenId,
  token0name,
  token1name,
	jsonRpcProvider,
	walletAddress
) {
  try {
    const MAX_UINT128 = "340282366920938463463374607431768211455";
    const positionManagerContract = new Contract(
      positionManagerAddress,
      positionManagerAbi,
      jsonRpcProvider
    );

    let token0, token1;
    if (
      (token0name === "USDT" && token1name === "XBR") ||
      (token0name === "XBR" && token1name === "USDT")
    ) {
      token0 = USDT_TOKEN;
      token1 = XBR_TOKEN;
    } else if (
      (token0name === "USDT" && token1name === "TBA") ||
      (token0name === "TBA" && token1name === "USDT")
    ) {
      token0 = TBA_TOKEN;
      token1 = USDT_TOKEN;
    } else {
      token0 = TBA_TOKEN;
      token1 = XBR_TOKEN;
    }

    const params = {
      tokenId,
      recipient: walletAddress,
      amount0Max: MAX_UINT128,
      amount1Max: MAX_UINT128,
    };

    const result = await positionManagerContract.collect.staticCall(params);

    const processedResult = [
      formatUnits(result[0].toString(), token0.decimals),
      formatUnits(result[1].toString(), token1.decimals),
    ];

    return processedResult;
  } catch (error) {
    console.error("Error at removing liquidity:", error);
  }
}
